import { useState } from 'react';
import { toast } from 'sonner';
import { TicketPercent } from 'lucide-react';
import { useMutation } from '@apollo/client/react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { BillingCouponForm } from 'src/components/Billing';
import { ApplyCouponDocument } from 'src/graphql/actions/billing/applyCoupon.mutation';
import { BillingDocument } from 'src/graphql/actions/billing/billing.query';
import { ClientError, getApiError } from 'src/utils/errors';
import { Button } from 'src/components/ui/button';

const AddCouponDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [applyCoupon] = useMutation(ApplyCouponDocument, {
    refetchQueries: [BillingDocument],
  });

  const onSubmit = async ({ code: couponCode }: { code: string }) => {
    try {
      const { data } = await applyCoupon({
        variables: {
          couponCode,
        },
      });

      if (data?.applyCoupon?.errors?.length) {
        const error =
          data.applyCoupon.errors[0] === 'Coupon does not exist'
            ? 'Sorry, this code is not valid or not applicable.'
            : data.applyCoupon.errors[0];
        throw new ClientError(error);
      } else {
        toast.success('Coupon added successfully!', {
          description: 'Your account will be updated at the next renewal date.',
        });
        setIsOpen(false);
      }
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  const onCancel = () => setIsOpen(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <TicketPercent />
          Apply coupon
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:w-[420px] gap-6">
        <DialogHeader>
          <DialogTitle className="text-base font-bold">Apply coupon</DialogTitle>
          <DialogDescription className="sr-only">Enter your coupon code:</DialogDescription>
        </DialogHeader>
        <BillingCouponForm onSubmit={onSubmit} onCancel={onCancel} />
      </DialogContent>
    </Dialog>
  );
};

export default AddCouponDialog;
